import {skipToken, SkipToken} from '@reduxjs/toolkit/query';

import {useMemo} from 'react';

import {isNotNil} from 'ramda';

import {useGetStatisticsV2Query} from '../../../store/priceReportApi';
import {GetStatisticsV2ApiArg} from '../../../types/api/api';
import {PriceReportType} from '../../../types/PriceReportType';
import {applyExactTotals, computeMarketMetrics, MarketMetrics} from '../utils/computeMarketMetrics';

export type UseMarketMetricsResult = {
  metrics: MarketMetrics | null;
  isLoading: boolean;
  isError: boolean;
};

/**
 * Market metrics for one statistics_v2 request. The main query returns at most 1000
 * (cheapest) cars; when the filter matches more, three tiny follow-up probes fetch
 * the exact figures the cheap page cannot provide:
 *  - advertised-only probe (size 1) → exact in-advertisement count,
 *  - sold-only probe (size 1)       → exact sold count,
 *  - advertised probe at the last offset → the true maximum price.
 * Metrics refine progressively as the probes land; min and page-end prices are
 * always exact since they only need the cheapest cars.
 */
export function useMarketMetrics(
  queryArg: GetStatisticsV2ApiArg | SkipToken,
  priceReport: PriceReportType
): UseMarketMetricsResult {
  const {data, isFetching, isError} = useGetStatisticsV2Query(queryArg);

  const isSkipped = queryArg === skipToken;
  const isTruncated =
    !isSkipped && isNotNil(data) && (data.total_hits ?? 0) > (data.cars?.length ?? 0);

  const advertisedProbeArg = useMemo(
    (): GetStatisticsV2ApiArg | SkipToken =>
      !isSkipped && isTruncated
        ? {
            reportId: queryArg.reportId,
            body: {
              ...queryArg.body,
              hideSoldIn30: true,
              hideSoldIn90: true,
              size: 1,
              offset: 0,
            },
          }
        : skipToken,
    [isSkipped, isTruncated, queryArg]
  );
  const {data: advertisedProbe} = useGetStatisticsV2Query(advertisedProbeArg);
  const advertisedTotal = advertisedProbe?.total_hits ?? null;

  const soldProbeArg = useMemo(
    (): GetStatisticsV2ApiArg | SkipToken =>
      !isSkipped && isTruncated
        ? {
            reportId: queryArg.reportId,
            body: {...queryArg.body, hideStockCars: true, size: 1, offset: 0},
          }
        : skipToken,
    [isSkipped, isTruncated, queryArg]
  );
  const {data: soldProbe} = useGetStatisticsV2Query(soldProbeArg);
  const soldTotal = soldProbe?.total_hits ?? null;

  const maxPriceProbeArg = useMemo(
    (): GetStatisticsV2ApiArg | SkipToken =>
      !isSkipped && isTruncated && isNotNil(advertisedTotal) && advertisedTotal > 0
        ? {
            reportId: queryArg.reportId,
            body: {
              ...queryArg.body,
              hideSoldIn30: true,
              hideSoldIn90: true,
              size: 1,
              offset: advertisedTotal - 1,
            },
          }
        : skipToken,
    [isSkipped, isTruncated, advertisedTotal, queryArg]
  );
  const {data: maxPriceProbe} = useGetStatisticsV2Query(maxPriceProbeArg);
  const advertisedMaxPrice = maxPriceProbe?.cars?.[0]?.price_with_vat ?? null;

  const metrics = useMemo(() => {
    if (isSkipped || !isNotNil(data) || isError) {
      return null;
    }
    const base = computeMarketMetrics(data, priceReport);
    if (!isTruncated) {
      return base;
    }
    return applyExactTotals(base, {
      advertisedCount: advertisedTotal,
      soldCount: soldTotal,
      advertisedMaxPrice,
    });
  }, [
    isSkipped,
    data,
    isError,
    priceReport,
    isTruncated,
    advertisedTotal,
    soldTotal,
    advertisedMaxPrice,
  ]);

  return {metrics, isLoading: isFetching, isError};
}
